import React, { useEffect } from 'react';
import { string, bool, func, number } from 'prop-types';
import classnames from 'classnames';
import { I18n, getContext } from '@fiverr-private/fiverr_context';
import { Button, Icon, Avatar } from '@fiverr-private/fit';
import { l_times, r_arrows_h } from '@fiverr-private/icons';
import { currencyFormat, dateTimeFormat, DATETIME_ELEMENT_STYLES } from '@fiverr-private/localization';
import { Base } from '@fiverr-private/modal';
import { sendMixPanelEvents, Events } from '../lib/mixpanelEvents';
import reportEvent, { BQ_EVENT_TYPES } from '../lib/reportEvent';
import { formatPercentage } from '../utils/formatting';
import { MODAL_WRAPPER_CLASS } from '../constants';
import { OrderIcon, ExpireIcon } from './assets';

import './style.scss';

const sendBigQueryEvent = (type, {
    userId,
    sellerId,
    discount,
    couponId,
    code,
    group,
    deliveryPackId,
    minOrderAmount,
    note
}) => {
    const { pageCtxId } = getContext();
    const actionType = group === 'user_action' ? {
        action: {
            type: 'click'
        }
    } : {};
    const extra = {
        group,
        entity: 'client',
        event_source: 'seller_page',
        feature: {
            name: 'coupon'
        },
        page: {
            name: 'seller_page',
            ctx_id: pageCtxId
        },
        coupon: {
            type: 'seller_coupon',
            invitee_user_id: userId,
            id: couponId,
            promo_code: code,
            invitation_id: deliveryPackId,
            discount_percent: discount / 100,
            minimum_amount: minOrderAmount,
            description: note
        },
        ...actionType
    };

    reportEvent(type, {
        userId,
        sellerId
    }, extra);
};

const CouponModal = ({
    code,
    couponId,
    deliveryPackId,
    discountValue,
    minOrderAmount,
    expiresAt,
    note = '',
    sellerName,
    sellerId,
    sellerImage,
    currentUserId,
    isVisible = false,
    onClose
}) => {
    const bigQueryParams = {
        userId: currentUserId,
        sellerId,
        discount: discountValue,
        couponId,
        code,
        expiresAt,
        deliveryPackId,
        minOrderAmount,
        note
    };

    useEffect(() => {
        sendMixPanelEvents(Events.Coupon.SEEN_MODAL, {
            'Flow': 'Receiving a coupon',
            'Buyer ID': currentUserId,
            'Seller ID': sellerId,
            'Coupon code': code,
            'Discount': discountValue
        });
        sendBigQueryEvent(BQ_EVENT_TYPES.SEEN_COUPON_MODAL, {
            group: 'user_impressions',
            ...bigQueryParams
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formattedDiscountValue = formatPercentage(discountValue);

    const formattedMinOrderAmount = currencyFormat(minOrderAmount / 100, 'USD', {
        fallbackValue: `$${minOrderAmount / 100}`
    });

    const expiryDate = dateTimeFormat(new Date(expiresAt), {
        year: DATETIME_ELEMENT_STYLES.NUMERIC,
        day: DATETIME_ELEMENT_STYLES.NUMERIC,
        month: DATETIME_ELEMENT_STYLES.SHORT,
        fallbackValue: expiresAt
    });

    return (
        <Base name={classnames(MODAL_WRAPPER_CLASS, 'coupon-modal')}
            autoOpen={isVisible}
            onClose={onClose}>
            {({ close }) => {

                const handleClose = () => {
                    close();
                    sendBigQueryEvent(BQ_EVENT_TYPES.CLICKED_COUPON_MODAL, {
                        group: 'user_action',
                        ...bigQueryParams
                    });
                };

                return (
                    <section className="coupon-modal-content-container">
                        <header className="t-a-center">
                            <button className="close-button"
                                type="button"
                                onClick={close}>
                                <Icon size={14}>{l_times}</Icon>
                            </button>
                            <h4>
                                <I18n k="user_page_perseus.discount_popup.caption"
                                    params={{ percentage: formattedDiscountValue }}/>
                            </h4>
                            <p>
                                <I18n k="user_page_perseus.discount_popup.description"
                                    params={{
                                        percentage: formattedDiscountValue,
                                        sellerName
                                    }}/>
                            </p>
                        </header>
                        <article>
                            <Avatar size={60} src={sellerImage} username={sellerName}/>
                            <div className="t-a-center">
                                <p className="m-t-12 m-b-8">
                                    <b>
                                        {
                                            note
                                                ? <I18n k="user_page_perseus.discount_popup.note_caption" params={{ sellerName }}/>
                                                : sellerName
                                        }
                                    </b>
                                </p>
                                {
                                    note &&
                                    <p className="tbody-6">
                                        {note}
                                    </p>
                                }
                            </div>
                            <div className="large-coupon">
                                <div className="discount-box">
                                    <div className="tbody-1">{formattedDiscountValue}</div>
                                    <div className="off-label"><I18n k="user_page_perseus.discount_popup.off"/></div>
                                </div>
                                <ul className="conditions-list p-r-8 p-t-8">
                                    <li>
                                        <Icon className="fit-icon" size={22}>{OrderIcon}</Icon>
                                        <I18n k="user_page_perseus.discount_popup.minimum_order_amount"
                                            params={{ minOrder: formattedMinOrderAmount }}
                                            parseMarkup/>
                                    </li>
                                    <li>
                                        <Icon className="fit-icon" size={18}>{r_arrows_h}</Icon>
                                        <I18n k="user_page_perseus.discount_popup.applies_to" parseMarkup/>
                                    </li>
                                    <li>
                                        <Icon className="fit-icon" size={22}>{ExpireIcon}</Icon>
                                        <I18n k="user_page_perseus.discount_popup.valid_until"
                                            params={{ date: expiryDate }}
                                            parseMarkup/>
                                    </li>
                                </ul>
                            </div>
                            <p className="co-grey-1000 tbody-7 m-t-24">
                                <I18n k="user_page_perseus.discount_popup.note"/>
                            </p>
                        </article>
                        <footer>
                            <Button className="confirm-button" onClick={handleClose}>
                                <I18n k="user_page_perseus.discount_popup.got_it"/>
                            </Button>
                        </footer>
                    </section>
                );
            }}
        </Base>
    );
};

CouponModal.propTypes = {
    code: string.isRequired,
    couponId: string.isRequired,
    deliveryPackId: string,
    discountValue: number.isRequired,
    expiresAt: string.isRequired,
    minOrderAmount: number.isRequired,
    note: string,
    sellerId: number.isRequired,
    sellerName: string.isRequired,
    sellerImage: string.isRequired,
    currentUserId: number.isRequired,
    isVisible: bool.isRequired,
    onClose: func.isRequired
};

export default CouponModal;
